// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { getCacheKeyFromConfigRef, getChildConfigRefs, sendAppError } from "../../../utilities/Utilities";
import { ClientSingleConfigResolver } from "./ClientSingleConfigResolver";
import { FallbackResolverAppErrors } from "../../../utilities/ResolverAppErrors";
/**
 * Client resolver class
 * This is a fallback scenarion when CRS is not available.
 */
export class ClientResolver {
    /**
     * Initialize client resolver based on fetching parameters.
     * @param {ResolverEnvironment} resolverEnvironment
     */
    constructor(resolverEnvironment) {
        /**
         * Dictionary to maintain all resolved config for internal state management
         * @type {Map<string, ExperienceConfig>}
         */
        this.mergedConfigMap = new Map();
        if (!resolverEnvironment
            || !resolverEnvironment.configServiceParameters
            || !resolverEnvironment.appType
            || !resolverEnvironment.configServiceParameters.configServiceBaseUrl
            || !resolverEnvironment.targetScope) {
            throw new Error("ClientResolver.ts: ResolverEnvironment or configServiceParameters or targetScope does not exist.");
        }
        this.resolverEnvironment = resolverEnvironment;
        this.appType = resolverEnvironment.appType;
        this.clientSingleConfigResolver = new ClientSingleConfigResolver(resolverEnvironment);
    }
    /**
     * Gets all the config in the config tree in a recursive manner.
     * @param {ConfigReference} entity entity for the root config.
     * @param {boolean} getFullTree indicate whether we want to load entire tree at once.
     * @returns {Map<string, ExperienceConfig>} returns the merged config map.
     */
    getConfig(entity, getFullTree = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const cacheKey = getCacheKeyFromConfigRef(entity, this.appType);
            // If this is not in the map, get the tree recursively and add all child configs to map
            if (!this.mergedConfigMap.has(cacheKey)) {
                yield this.getConfigRecursive(entity, getFullTree);
                const allNullFetches = this.clientSingleConfigResolver.getFailedFetches();
                if (allNullFetches.length > 0) {
                    allNullFetches.forEach((nullFetchUrl) => {
                        sendAppError(this.resolverEnvironment.appErrorCallback, Object.assign(Object.assign({}, FallbackResolverAppErrors.BlobFetchReturnedNull), { message: "Fetch failed for a config", pb: Object.assign(Object.assign({}, FallbackResolverAppErrors.BlobFetchReturnedNull.pb), { customMessage: `Failed fetch url: ${nullFetchUrl}` }) }));
                    });
                }
            }
            return this.mergedConfigMap.get(cacheKey);
        });
    }
    /**
     * Get configMap downloaded by fallback resolver.
     */
    getConfigMap() {
        return this.mergedConfigMap;
    }
    /**
     * Helper function for getConfig. Gets all the config in the config tree in a recursive manner.
     * @param {ConfigReference} entity entity for the root config.
     * @param {boolean} getFullTree indicate whether we want to load entire tree at once.
     */
    getConfigRecursive(entity, getFullTree = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const cacheKey = getCacheKeyFromConfigRef(entity, this.appType);
            if (!cacheKey || this.mergedConfigMap.has(cacheKey)) {
                return;
            }
            try {
                // root of specific sub tree
                const mergedConfig = yield this.clientSingleConfigResolver.resolveConfigAsync(entity);
                // add to memory even if it is undefined because we don't want to fetch fail states multiple times
                this.mergedConfigMap.set(cacheKey, mergedConfig);
                // Only fetch rest of the tree if requested.
                if (getFullTree) {
                    const unresolvedConfigRefs = getChildConfigRefs(mergedConfig);
                    if (unresolvedConfigRefs.size == 0) {
                        return;
                    }
                    const configTasks = [];
                    unresolvedConfigRefs.forEach(entity => {
                        configTasks.push(this.getConfigRecursive(entity, getFullTree));
                    });
                    // if a single config fails, it fails all, exists today as well
                    yield Promise.all(configTasks);
                }
            }
            catch (error) {
                sendAppError(this.resolverEnvironment.appErrorCallback, Object.assign(Object.assign({}, FallbackResolverAppErrors.RecursiveGetFailed), { message: "Exception in getConfig.", pb: Object.assign(Object.assign({}, FallbackResolverAppErrors.RecursiveGetFailed.pb), { customMessage: `CacheKey: ${cacheKey} / Error: ${error}` }) }));
            }
        });
    }
}
//# sourceMappingURL=ClientResolver.js.map